import { DomainError } from "./errors";
import { isUf } from "./dashboard";
import {
  EXTERIOR,
  ICMS_INTERNAL_RATE_PCT4,
  REFORM_2026_RATES,
  isCsosn,
  isIbsCbsCst,
  isIcmsCst,
  isIpiCst,
  isPisCofinsCst,
  isSimplesCrt,
  type Crt,
  type TaxDirection,
  type TaxRegime,
  type TaxRule,
} from "./tax";
import type { Uf } from "./dashboard";

// ===== Naturezas de operação (RF-VEN-04): validação das regras e assistente =====

/** Naturezas por empresa (ativas e inativas: nada se exclui). */
export const MAX_TAX_OPERATIONS = 200;
export const MAX_TAX_RULES_PER_OPERATION = 500;

export const TAX_REGIMES = ["simples", "mei", "presumido", "real"] as const satisfies readonly TaxRegime[];
/** Faixas de faturamento dos últimos 12 meses do assistente (Anexos do Simples; acima de 4,8 mi fora do Simples). */
export const REVENUE_BANDS = [
  "ate_180k",
  "ate_360k",
  "ate_720k",
  "ate_1800k",
  "ate_3600k",
  "ate_4800k",
  "acima_4800k",
] as const;
export type RevenueBand = (typeof REVENUE_BANDS)[number];
export const BUSINESS_BRANCHES = ["comercio", "industria"] as const;
export type BusinessBranch = (typeof BUSINESS_BRANCHES)[number];
export const WIZARD_ACTIVITIES = [
  "compra_venda",
  "st",
  "import_export",
  "transferencia",
  "remessas",
  "devolucao",
  "bonificacao",
] as const;
export type WizardActivity = (typeof WIZARD_ACTIVITIES)[number];

/** Regra sem identidade (o DO dá o id e a situação). */
export type TaxRuleDraft = Omit<TaxRule, "id" | "active" | "createdAt">;

const fail = (code: string, message: string): never => {
  throw new DomainError(code, message);
};

/**
 * CRT pelo regime e faixa: MEI = 4; Simples até 3,6 mi = 1, de 3,6 a 4,8 mi = 2 (excesso de sublimite: ICMS fora do
 * Simples); Presumido/Real = 3. Simples acima de 4,8 mi e MEI acima de R$ 81 mil = `validation`.
 */
export function crtFor(regime: TaxRegime, band: RevenueBand): Crt {
  if (regime === "mei") {
    if (band !== "ate_180k")
      fail("validation", "O MEI fatura até R$ 81 mil por ano: escolha a primeira faixa.");
    return 4;
  }
  if (regime === "simples") {
    if (band === "acima_4800k")
      fail("validation", "Acima de R$ 4,8 milhões a empresa não pode estar no Simples Nacional.");
    return band === "ate_4800k" ? 2 : 1;
  }
  return 3;
}

/**
 * Problemas semânticos da regra (o formato vem do contrato): CFOP da direção, CST × CSOSN coerente com a condição de
 * CRT, ST com MVA, códigos de IPI/PIS/COFINS/IBS-CBS suportados pelo motor, classificação do IBS/CBS, NCM e UF.
 * `null` = válida. Campos = chaves do contrato.
 */
export function taxRuleProblems(r: TaxRuleDraft, direction: TaxDirection): Record<string, string> | null {
  const p: Record<string, string> = {};
  const firsts = direction === "out" ? "567" : "123";
  if (!/^\d{4}$/.test(r.cfop) || !firsts.includes(r.cfop[0]!))
    p.cfop =
      direction === "out"
        ? "CFOP de saída com 4 dígitos começando em 5, 6 ou 7"
        : "CFOP de entrada com 4 dígitos começando em 1, 2 ou 3";
  if (r.icmsCst === null && r.icmsCsosn === null) p.icmsCst = "Informe o CST ou o CSOSN do ICMS";
  if (r.icmsCst !== null && !isIcmsCst(r.icmsCst)) p.icmsCst = "CST de ICMS não suportado";
  if (r.icmsCsosn !== null && !isCsosn(r.icmsCsosn)) p.icmsCsosn = "CSOSN não suportado";
  if (r.crt !== null && isSimplesCrt(r.crt) && r.icmsCsosn === null)
    p.icmsCsosn = "Regra do Simples Nacional (CRT 1 ou 4) precisa de CSOSN";
  if (r.crt !== null && !isSimplesCrt(r.crt) && r.icmsCst === null)
    p.icmsCst = "Regra do regime normal (CRT 2 ou 3) precisa de CST";
  const st =
    ["10", "30", "70"].includes(r.icmsCst ?? "") || ["201", "202", "203"].includes(r.icmsCsosn ?? "");
  if (st && r.icmsStModBc !== 6 && r.icmsStMvaPct4 === null)
    p.icmsStMvaPct4 = "Informe a MVA da substituição tributária";
  if (r.icmsStMvaAdjust && (r.icmsStPct4 ?? 0) >= 1_000_000)
    p.icmsStPct4 = "Com MVA ajustada, a alíquota interna do ST deve ser menor que 100%";
  if (r.difalDoubleBase && (r.icmsDestPct4 ?? 0) + (r.fcpDestPct4 ?? 0) >= 1_000_000)
    p.fcpDestPct4 = "Alíquota do destino + FCP deve ser menor que 100%";
  if (r.ipiCst !== null && !isIpiCst(r.ipiCst)) p.ipiCst = "CST de IPI não suportado";
  if (r.ipiEnq !== null && !/^\d{3}$/.test(r.ipiEnq)) p.ipiEnq = "Código de enquadramento com 3 dígitos";
  if (r.pisCst !== null && !isPisCofinsCst(r.pisCst)) p.pisCst = "CST de PIS não suportado";
  if (r.cofinsCst !== null && !isPisCofinsCst(r.cofinsCst)) p.cofinsCst = "CST de COFINS não suportado";
  if (r.ibsCst !== null && !isIbsCbsCst(r.ibsCst)) p.ibsCst = "CST de IBS/CBS não suportado";
  if (r.ibsCst === null && r.ibsClass !== null) p.ibsCst = "Informe o CST do IBS/CBS da classificação";
  if (r.ibsCst !== null && isIbsCbsCst(r.ibsCst)) {
    if (r.ibsClass === null && r.ibsCst !== "000")
      p.ibsClass = "Informe a classificação tributária (cClassTrib)";
    if (r.ibsClass !== null && (!/^\d{6}$/.test(r.ibsClass) || r.ibsClass.slice(0, 3) !== r.ibsCst))
      p.ibsClass = "cClassTrib com 6 dígitos começando pelo CST";
  }
  if (r.ncmPrefix !== null && !/^\d{2,8}$/.test(r.ncmPrefix)) p.ncmPrefix = "NCM (início) de 2 a 8 dígitos";
  if (r.destUf !== null && r.destUf !== EXTERIOR && !isUf(r.destUf)) p.destUf = "UF inválida";
  return Object.keys(p).length ? p : null;
}

// ---------- assistente ----------

export type TaxWizardInput = {
  regime: TaxRegime;
  revenueBand: RevenueBand;
  branch: BusinessBranch;
  activities: readonly WizardActivity[];
  /** % de crédito de ICMS do Simples (pCredSN); null = CSOSN 102 (sem crédito). */
  creditSnPct4: number | null;
};

export type TaxOperationDraft = {
  name: string;
  direction: TaxDirection;
  /** finNFe: 1 normal, 4 devolução. */
  purpose: 1 | 2 | 3 | 4;
  docModel: "55" | "65";
  presenceIndicator: number;
  postStock: boolean;
  postFinancial: boolean;
  isDefault: boolean;
  additionalInfo: string | null;
  rules: TaxRuleDraft[];
};

const EMPTY_RULE: TaxRuleDraft = {
  priority: 0,
  destUf: null,
  destIeIndicator: null,
  destFinalConsumer: null,
  crt: null,
  ncmPrefix: null,
  productId: null,
  productGroupId: null,
  productOrigin: null,
  cfop: "5102",
  icmsCst: null,
  icmsCsosn: null,
  icmsPct4: null,
  icmsReductionPct4: null,
  icmsCreditSnPct4: null,
  icmsDeferralPct4: null,
  fcpPct4: null,
  icmsStModBc: null,
  icmsStMvaPct4: null,
  icmsStMvaAdjust: false,
  icmsStPct4: null,
  icmsStReductionPct4: null,
  fcpStPct4: null,
  difal: true,
  difalDoubleBase: false,
  icmsDestPct4: null,
  fcpDestPct4: null,
  ipiCst: null,
  ipiPct4: null,
  ipiEnq: null,
  pisCst: null,
  pisPct4: null,
  cofinsCst: null,
  cofinsPct4: null,
  pisCofinsExcludeIcms: true,
  ibsCst: null,
  ibsClass: null,
  ibsUfPct4: null,
  ibsMunPct4: null,
  cbsPct4: null,
  ibsReductionPct4: null,
  cbsReductionPct4: null,
  ibsDeferralPct4: null,
  isPct4: null,
  infoText: null,
};

const SIMPLES_INFO =
  "Documento emitido por ME ou EPP optante pelo Simples Nacional. Não gera direito a crédito fiscal de IPI.";

/**
 * Naturezas e regras padrão do assistente (RF-VEN-04). Uma regra "qualquer destino" por natureza: o motor já ajusta
 * CFOP (5/6/7, 6107/6108), alíquota interestadual e DIFAL pelo destinatário; o usuário refina por UF/NCM depois.
 * CRT 3 ganha o grupo IBS/CBS com as alíquotas de teste de 2026; Simples/MEI/CRT 2 não (opcional em 2026).
 * Códigos cClassTrib das naturezas não onerosas (410001 bonificação, 410002 transferência, 410004 exportação) a
 * conferir na tabela vigente.
 */
export function taxWizardPlan(i: TaxWizardInput, uf: Uf): { crt: Crt; operations: TaxOperationDraft[] } {
  const crt = crtFor(i.regime, i.revenueBand);
  const acts = new Set(i.activities);
  if (acts.size === 0) fail("validation", "Escolha ao menos uma atividade");
  const simples = isSimplesCrt(crt);
  const industry = i.branch === "industria";
  const ibs = (cst: string, cls: string): Partial<TaxRuleDraft> =>
    crt === 3
      ? {
          ibsCst: cst,
          ibsClass: cls,
          ...(cst === "000"
            ? {
                ibsUfPct4: REFORM_2026_RATES.ibsUfPct4,
                ibsMunPct4: REFORM_2026_RATES.ibsMunPct4,
                cbsPct4: REFORM_2026_RATES.cbsPct4,
              }
            : {}),
        }
      : {};
  /** Saída tributada como venda (CST 00 com a alíquota interna da UF; Simples 101/102). */
  const taxed = (cfop: string, o: Partial<TaxRuleDraft> = {}): TaxRuleDraft => ({
    ...EMPTY_RULE,
    cfop,
    ...(simples
      ? i.creditSnPct4 && crt === 1
        ? { icmsCsosn: "101", icmsCreditSnPct4: i.creditSnPct4 }
        : { icmsCsosn: "102" }
      : { icmsCst: "00", icmsPct4: ICMS_INTERNAL_RATE_PCT4[uf] }),
    ...(industry && !simples ? { ipiCst: "50" } : {}),
    ...ibs("000", "000001"),
    ...o,
  });
  /** Sem ICMS (CST/CSOSN conforme o regime). */
  const free = (cfop: string, cst: string, csosn: string, o: Partial<TaxRuleDraft> = {}): TaxRuleDraft => ({
    ...EMPTY_RULE,
    cfop,
    ...(simples ? { icmsCsosn: csosn } : { icmsCst: cst }),
    ...o,
  });
  const op = (
    name: string,
    rules: TaxRuleDraft[],
    o: Partial<Omit<TaxOperationDraft, "name" | "rules">> = {},
  ): TaxOperationDraft => ({
    name,
    direction: "out",
    purpose: 1,
    docModel: "55",
    presenceIndicator: 1,
    postStock: true,
    postFinancial: true,
    isDefault: false,
    additionalInfo: simples ? SIMPLES_INFO : null,
    rules,
    ...o,
  });
  const sale = industry ? "5101" : "5102";
  const ops: TaxOperationDraft[] = [];
  if (acts.has("compra_venda")) {
    ops.push(op("Venda de mercadoria", [taxed(sale)], { isDefault: true }));
    ops.push(op("Venda para consumidor final", [taxed(sale)], { presenceIndicator: 1 }));
  }
  if (acts.has("st"))
    ops.push(
      op("Venda de mercadoria com ST retido anteriormente", [
        free("5405", "60", "500", {
          ...ibs("000", "000001"),
          infoText: "ICMS retido anteriormente por substituição tributária.",
        }),
      ]),
    );
  if (acts.has("import_export"))
    ops.push(
      op("Venda para o exterior", [
        free(industry ? "7101" : "7102", "41", "300", {
          ...(industry && !simples ? { ipiCst: "54" } : {}),
          ...(crt === 3 ? { pisCst: "08", cofinsCst: "08" } : {}),
          ...ibs("410", "410004"),
          infoText: "Exportação: não incidência do ICMS (CF art. 155 §2º X, a) e imunidade do IPI.",
        }),
      ]),
    );
  if (acts.has("transferencia"))
    ops.push(
      op(
        "Transferência entre estabelecimentos",
        [
          free(industry ? "5151" : "5152", "41", "400", {
            ...ibs("410", "410002"),
            infoText:
              "Transferência entre estabelecimentos do mesmo titular: não incidência do ICMS (STF ADC 49; LC 204/2023).",
          }),
        ],
        { postFinancial: false },
      ),
    );
  if (acts.has("remessas")) {
    ops.push(
      op(
        "Remessa para conserto ou reparo",
        [free("5915", "50", "400", { infoText: "Suspensão do ICMS: remessa para conserto ou reparo." })],
        {
          postFinancial: false,
        },
      ),
    );
    ops.push(
      op("Remessa em demonstração", [free("5912", "40", "400", { infoText: "Remessa em demonstração." })], {
        postFinancial: false,
      }),
    );
  }
  if (acts.has("devolucao")) {
    const back = (cfop: string, pis: string): TaxRuleDraft =>
      simples
        ? free(cfop, "00", "900", { pisCst: pis, cofinsCst: pis })
        : { ...taxed(cfop), ipiCst: null, pisCst: pis, cofinsCst: pis };
    ops.push(
      op("Devolução de compra", [back(industry ? "5201" : "5202", "49")], {
        purpose: 4,
        postFinancial: false,
      }),
    );
    ops.push(
      op("Devolução de venda", [back(industry ? "1201" : "1202", "99")], {
        direction: "in",
        purpose: 4,
        postFinancial: false,
      }),
    );
  }
  if (acts.has("bonificacao"))
    ops.push(
      op("Bonificação, doação ou brinde", [taxed("5910", ibs("410", "410001"))], { postFinancial: false }),
    );
  return { crt, operations: ops };
}
